import { isDefined, nullifyEmptyStrings } from '@/composables/utils'
import { EndPoint } from '@/enums/EndPoint'
import type { StoredProcedure } from '@/enums/StoredProcedure'
import type { ICollectionResponse } from '@/interfaces/api/ICollectionResponse'
import type { IFieldSearchOptions } from '@/interfaces/api/IFieldSearchOptions'
import type { IItemResponse } from '@/interfaces/api/IItemResponse'
import type { IQueryFilter } from '@/interfaces/api/IQueryFilter'
import type { IQueryOptions } from '@/interfaces/api/IQueryOptions'
import type { TableRecord } from '@/models/TableRecord'
import { useSnackbarStore } from '@/stores/ui/SnackbarStore'
import { getAuthInstance } from '@fusion/auth-v3'
import axios from 'axios'
import { defineStore } from 'pinia'
import { useSettingsStore } from '../ui/SettingsStore'

export const useAPIStore = defineStore('api', () => {
  // STORES, IMPORTS, & COMPOSABLES
  const auth0 = getAuthInstance()
  const http = axios.create({
    baseURL: APP_ENV.VITE_APP_FITPRO_HOST,
    timeout: 60000
  })
  const settingsStore = useSettingsStore()
  const snackbarStore = useSnackbarStore()

  // FUNCTIONS - PRIVATE
  /**
   * Get the HTTP headers for the API request.
   * @returns
   */
  async function _getHTTPHeaders() {
    const token = await auth0.getTokenSilently()
    const headers: Record<string, any> = {
      Authorization: `Bearer ${token}`
    }
    if (isDefined(settingsStore.activeDatasetId)) {
      headers['x-dataset-id'] = settingsStore.activeDatasetId
    }
    return headers
  }

  function _getErrorResponse(errorResponse: any): any {
    if (typeof errorResponse.data === 'string') {
      return { name: 'Error', message: errorResponse.data, status: errorResponse.status }
    }
    return {
      name: errorResponse.data.error.name ?? errorResponse.data.name,
      message: errorResponse.data.error.message ?? errorResponse.data.message,
      status: errorResponse.status
    }
  }

  // FUNCTIONS - PUBLIC
  async function deleteOne(
    endpoint: EndPoint,
    id: number,
    showError: boolean = true
  ): Promise<IItemResponse> {
    const url = `${endpoint}/${id}`
    const headers = await _getHTTPHeaders()
    let response
    try {
      response = await http.delete(url, { headers })
      return { data: response.data, error: null }
    } catch (error: any) {
      if (showError) {
        snackbarStore.addAxiosMessage(endpoint, error)
      }
      return { data: null, error: _getErrorResponse(error.response) }
    }
  }

  async function deleteMany(
    endpoint: EndPoint,
    queryFilter: Array<IQueryFilter>,
    showError: boolean = true
  ): Promise<ICollectionResponse> {
    const headers = await _getHTTPHeaders()

    try {
      const url = `${endpoint}`
      const response = await http.delete(url, { data: { where: queryFilter }, headers })
      return { data: response.data, error: null }
    } catch (error: any) {
      if (showError) {
        snackbarStore.addAxiosMessage(endpoint, error)
      }
      return {
        data: { results: [], count: 0 },
        error: _getErrorResponse(error.response)
      }
    }
  }

  async function execute(
    command: StoredProcedure,
    args: Array<string | boolean | number>,
    showError: boolean = true
  ): Promise<ICollectionResponse> {
    const headers = await _getHTTPHeaders()

    let response
    try {
      response = await http.post('exec', { command, args }, { headers })
      return { data: { results: response.data?.results, count: response.data.count }, error: null }
    } catch (error: any) {
      if (showError) {
        snackbarStore.addAxiosMessage('exec', error)
      }
      return {
        data: { results: [], count: 0 },
        error: _getErrorResponse(error.response)
      }
    }
  }

  async function getOne(
    endpoint: EndPoint,
    id: number,
    fields: Array<string> = [],
    showError: boolean = true
  ): Promise<IItemResponse> {
    let url = `${endpoint}/${id}`
    if (fields.length > 0) {
      url = `${url}?fields=${fields.join(',')}`
    }
    const headers = await _getHTTPHeaders()
    let response
    try {
      response = await http.get(url, { headers })
      nullifyEmptyStrings(response.data, endpoint)
      return { data: response.data, error: null }
    } catch (error: any) {
      if (showError) {
        snackbarStore.addAxiosMessage(endpoint, error)
      }
      return { data: null, error: _getErrorResponse(error.response) }
    }
  }

  async function getMany(
    endpoint: EndPoint,
    queryOptions?: IQueryOptions,
    showError: boolean = true
  ): Promise<ICollectionResponse> {
    const headers = await _getHTTPHeaders()

    try {
      let response
      if (queryOptions) {
        const url: string = `${endpoint}/query-builder`
        response = await http.post(url, queryOptions, { headers })
        return { data: response.data, error: null }
      } else {
        const url = `${endpoint}`
        response = await http.get(url, { headers })
        nullifyEmptyStrings(response.data, endpoint)
        return { data: response.data, error: null }
      }
    } catch (error: any) {
      if (showError) {
        snackbarStore.addAxiosMessage(endpoint, error)
      }
      return {
        data: { results: [], count: 0 },
        error: _getErrorResponse(error.response)
      }
    }
  }

  async function post(
    url: string,
    payload: Object = {},
    showError: boolean = true
  ): Promise<IItemResponse> {
    const headers = await _getHTTPHeaders()
    try {
      const response = await http.post(url, payload, { headers })
      return response.data
    } catch (error: any) {
      if (showError) {
        snackbarStore.addAxiosMessage(url, error)
      }
      return { data: null, error: _getErrorResponse(error.response) }
    }
  }

  async function save(
    endpoint: EndPoint,
    item: TableRecord,
    showError: boolean = true
  ): Promise<IItemResponse> {
    let response
    const headers = await _getHTTPHeaders()
    try {
      nullifyEmptyStrings(item, endpoint)
      if (item.id) {
        const url = `${endpoint}/${item.id}`
        response = await http.patch(url, item, { headers })
      } else {
        const url = `${endpoint}`
        response = await http.post(url, item, { headers })
      }
      return { data: response.data, error: null }
    } catch (error: any) {
      if (showError) {
        snackbarStore.addAxiosMessage(endpoint, error)
      }
      return { data: null, error: _getErrorResponse(error.response) }
    }
  }

  async function createMany(
    endpoint: EndPoint,
    items: Array<TableRecord>,
    showError: boolean = true
  ): Promise<IItemResponse> {
    let response
    const headers = await _getHTTPHeaders()
    try {
      items.forEach((item) => {
        nullifyEmptyStrings(item, endpoint)
      })
      const url = `${endpoint}`
      response = await http.post(url, items, { headers })
      return { data: response.data, error: null }
    } catch (error: any) {
      if (showError) {
        snackbarStore.addAxiosMessage(endpoint, error)
      }
      return { data: null, error: _getErrorResponse(error.response) }
    }
  }

  async function search(
    endpoint: EndPoint,
    searchOptions: IFieldSearchOptions,
    showError: boolean = true
  ): Promise<ICollectionResponse> {
    const headers = await _getHTTPHeaders()
    const url: string = `${endpoint}/search`
    try {
      const response = await http.post(url, searchOptions, { headers })
      nullifyEmptyStrings(response.data, endpoint)
      return { data: response.data, error: null }
    } catch (error: any) {
      if (showError) {
        snackbarStore.addAxiosMessage(endpoint, error)
      }
      return { data: { results: [], count: 0 }, error: _getErrorResponse(error.response) }
    }
  }

  async function updateMany(
    endpoint: EndPoint,
    queryOptions?: IQueryOptions,
    showError: boolean = true
  ): Promise<ICollectionResponse> {
    const headers = await _getHTTPHeaders()

    try {
      let response
      const url = `${endpoint}`
      if (queryOptions?.update) {
        nullifyEmptyStrings(queryOptions.update, endpoint)
        response = await http.patch(url, queryOptions, { headers })
        return { data: response.data, error: null }
      } else {
        response = await http.patch(url, { headers })
        return { data: response.data, error: null }
      }
    } catch (error: any) {
      if (showError) {
        snackbarStore.addAxiosMessage(endpoint, error)
      }
      return { data: { results: [], count: 0 }, error: _getErrorResponse(error.response) }
    }
  }

  return {
    deleteOne,
    createMany,
    deleteMany,
    execute,
    getOne,
    getMany,
    post,
    save,
    search,
    updateMany
  }
})
